import React, { useCallback, useRef, useState} from "react";
import ChatPanel from "../../search/components/ChatPanel";
import ReviewCard from "../../../features/review/ReviewCard";
import ReviewCardSkeleton from "../../../features/review/ReviewCardSkeleton";
import { useChatSearch } from "../../search/hooks/useChatSearch";
import type { ChatMsg } from "../../search/components/ChatPanel";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { useAiCompareSSE } from "../../search/hooks/useAiCompareSSE";
import { CompareMarkdownView } from "../components/CompareMarkdownView";
import PixelHumanWalkers from "../components/PixelHuman";
import { useLocation } from "react-router-dom";


type Props = {
  keyword: string;
  setKeyword: React.Dispatch<React.SetStateAction<string>>;
};

const SearchResultsPage: React.FC<Props> = ({ keyword }) => {
  const [activeTab, setActiveTab] = useState<"list" | "compare">("list");
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const keywordFromQuery = params.get("keyword") ?? "";

  // /search-entry の検索窓入力（クエリ）を最優先
  const baseKeyword = keywordFromQuery || keyword || "";
  const initialText = useRef(baseKeyword || "キャンプ用品を探したい").current;

  const {
      messages,
      chips,
      cards,
      isLoading,
      hasShownFirstList,
      selectedChips,
      handleUserMessage,
      handleChipSelect,
  } = useChatSearch(initialText);


  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { blocks, loading, done, startCompare } = useAiCompareSSE();
  console.log("compare blocks", blocks);
  console.log("loading", loading, "done", done);

  const buildAmazonUrl = () => {
      const base = initialText.trim();
      const conds = selectedChips.map((v) => v.trim()).filter(Boolean);

      const q = [base, ...conds].filter(Boolean).join(" ").trim();
      if (!q) return "";

      return `https://www.amazon.co.jp/s?k=${encodeURIComponent(q)}&tag=afrofair-22`;
  };


  const handleSelectChange = useCallback((id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleCompare = () => {
    if (selectedIds.size < 2) {
      alert("比較する商品を2つ以上選んでください");
      return;
    }
    setActiveTab("compare");

    startCompare(Array.from(selectedIds), cards);
  };

  return (
    <PanelGroup direction="horizontal" className="w-full h-full bg-[#f5f7fb]">
      {/* 左：チャット */}
      <Panel defaultSize={35} minSize={20}>
        <ChatPanel
          messages={messages as ChatMsg[]}
          onSend={handleUserMessage}
          belowMessages={
            isLoading ? (
              <PixelHumanWalkers  />
            ) : chips.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-3">
                {chips.map((chip, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleChipSelect(chip)}
                    className="px-4 py-1.5 text-sm border border-gray-300 rounded-full bg-white hover:bg-gray-100 transition"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            ) : null
          }
        />
      </Panel>
      {/* 可変 */}
      <PanelResizeHandle className="w-1 bg-gray-300 hover:bg-gray-400 cursor-col-resize" />
      {/* 右：商品結果 */}
      <Panel defaultSize={65} minSize={30}>
        <div className="p-3 bg-gray-50 border-b sticky top-0 z-10">
          {/* タブ + 比較ボタン */}
          <div className="flex items-center justify-between">
            {/* 左：タブ */}
            <div className="flex gap-4">
              {/* リストタブ */}
              <button
                onClick={() => setActiveTab("list")}
                className={`
                  px-3 py-2 rounded-t-md transition
                  ${activeTab === "list"
                    ? "font-bold border-b-2 border-blue-600 text-blue-600 bg-white"
                    : "text-gray-500 hover:text-gray-700"}
                `}
              >
                リスト
              </button>

              {/* 比較タブ */}
              <button
                onClick={() => setActiveTab("compare")}
                className={`
                  px-3 py-2 rounded-t-md transition
                  ${activeTab === "compare"
                    ? "font-bold border-b-2 border-blue-600 text-blue-600 bg-white"
                    : "text-gray-500 hover:text-gray-700"}
                `}
              >
                比較（{selectedIds.size}）
              </button>
            </div>
            {/* 右：比較ボタン（リストタブのときだけ表示） */}
            {activeTab === "list" ? (
              <div className="flex items-center gap-3">
                <a
                    href={buildAmazonUrl() || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`
                        px-4 py-2 rounded shadow-lg transition border
                        ${buildAmazonUrl()
                            ? "bg-white text-gray-800 hover:bg-gray-100 border-gray-300"
                            : "bg-gray-200 text-gray-400 border-gray-200 pointer-events-none"}
                    `}
                >
                    この検索条件でAmazonの商品を見てみる
                </a>

                <button
                  onClick={handleCompare}
                  className="px-4 py-2 bg-blue-600 text-white rounded shadow-lg hover:bg-blue-700 transition"
                >
                  選択した商品を比較
                </button>
              </div>
            ) : (
              <div className="px-4 py-2 invisible">ダミー</div>
            )}
          </div>
        </div>

        <div className="min-w-0 flex-1 overflow-y-auto p-3 bg-gray-50 h-[calc(100vh-88px)]">
          {/* === リストタブ === */}
          {activeTab === "list" && (
            <>
              {!hasShownFirstList ? (
                <div>
                  {/* ローディングテキスト */}
                  <div className="flex items-center gap-2 text-gray-500 mb-4 px-1">
                    <span className="animate-pulse">商品を探しています</span>
                    <span className="animate-bounce">.</span>
                    <span className="animate-bounce [animation-delay:0.1s]">.</span>
                    <span className="animate-bounce [animation-delay:0.2s]">.</span>
                  </div>

                  {/* スケルトンカード */}
                  <ReviewCardSkeleton />
                  <ReviewCardSkeleton />
                  <ReviewCardSkeleton />
                  <ReviewCardSkeleton />
                  <ReviewCardSkeleton />
                </div>
              ) : cards.length === 0 ? (
                // ★ ローディングでなければ「まだ商品が見つかってません」
                <p className="text-gray-500 mt-6">まだ商品が見つかっていません。</p>
              ) : (
                cards.map((item, index) => (
                  <ReviewCard
                    key={item.id}
                    item={item}
                    index={index}
                    selected={selectedIds.has(item.id)}
                    onSelectChange={handleSelectChange}
                  />
                ))
              )}
            </>
          )}

          {/* === 比較タブ === */}
          {activeTab === "compare" && (
            <div className="h-full overflow-y-auto p-4 bg-gray-50">

              {/* 読み込み中 */}
              {loading && blocks.length === 0 && (
                <div className="text-gray-500 mb-4">
                  比較結果を生成しています…
                </div>
              )}

              {/* メッセージ出力 */}
              <div className="flex items-start gap-3 bg-white p-6 rounded-lg shadow">
                <div className="w-8 h-8 bg-gray-200 rounded-full" />

                <div className="prose prose-slate max-w-none">
                  {/* SSE完了後：Markdown整形表示 */}
                  <div className="prose prose-slate max-w-none">
                    {blocks.map((block, i) => (
                      <CompareMarkdownView key={i} markdown={block} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Panel>
    </PanelGroup>
  );
};

export default SearchResultsPage;
